import { useInfiniteQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { apiService } from "../../services/api";
import { PaginatedResponse, RecentMessage } from "../../types/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent } from "../ui/dialog";
import { useEffect, useRef, useState } from "react";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Music,
  Video,
  Image,
  MessageSquare,
} from "lucide-react";
import { SuccessResponse } from "../../types/generated";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

const StatusBadge = ({ status, error_details }: { status: string, error_details?: string }) => {
  switch (status.toLowerCase()) {
    case "sent":
      return (
        <Badge className="bg-green-500">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Sent
        </Badge>
      );
    case "failed":
      return error_details ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="destructive" className="cursor-pointer">
                <XCircle className="mr-1 h-3 w-3" />
                Failed
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {error_details}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Badge variant="destructive">
          <XCircle className="mr-1 h-3 w-3" />
          Failed
        </Badge>
      );
    case "pending":
      return (
        <Badge variant="secondary">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      );
    default:
      return <Badge>{status}</Badge>;
  }
};

interface MediaPreviewProps {
  message: RecentMessage;
  onClick: () => void;
}

const MediaPreview = ({ message, onClick }: MediaPreviewProps) => {
  const media =
    message.content.image || message.content.video || message.content.audio;
  if (!media?.thumbnail && !media?.link) return null;

  return (
    <div
      onClick={onClick}
      className="mt-2 cursor-pointer hover:opacity-80 transition-opacity"
    >
      {media.thumbnail ? (
        <img
          src={media.thumbnail}
          alt={media.caption || "Media preview"}
          className="w-20 h-20 object-cover rounded-md"
        />
      ) : (
        <div className="w-20 h-20 bg-muted rounded-md flex items-center justify-center">
          {message.content.type === "video" && (
            <Video className="h-8 w-8 text-muted-foreground" />
          )}
          {message.content.type === "audio" && (
            <Music className="h-8 w-8 text-muted-foreground" />
          )}
        </div>
      )}
    </div>
  );
};

interface MediaDialogProps {
  message: RecentMessage | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MediaDialog = ({ message, open, onOpenChange }: MediaDialogProps) => {
  if (!message) return null;

  const media =
    message.content.image || message.content.video || message.content.audio;
  if (!media?.link) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 overflow-hidden w-fit max-w-[90vw] max-h-[90vh]">
        {message.content.type === "image" && (
          <div className="relative">
            <img
              src={media.link}
              alt={media.caption || "Media"}
              className="rounded-lg max-w-[85vw] max-h-[85vh] object-contain"
            />
            {media.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 p-4 rounded-b-lg">
                <p className="text-sm text-white text-center">
                  {media.caption}
                </p>
              </div>
            )}
          </div>
        )}
        {message.content.type === "video" && (
          <div className="relative max-w-[85vw] max-h-[85vh]">
            <video
              src={media.link}
              controls
              className="rounded-lg w-full h-full"
            />
            {media.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 p-4">
                <p className="text-sm text-white text-center">
                  {media.caption}
                </p>
              </div>
            )}
          </div>
        )}
        {message.content.type === "audio" && (
          <div className="bg-muted p-6 rounded-lg w-[400px] max-w-[85vw]">
            <audio src={media.link} controls className="w-full" />
            {media.caption && (
              <p className="mt-4 text-sm text-muted-foreground text-center">
                {media.caption}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const RecentMessages = () => {
  const [selectedMessage, setSelectedMessage] = useState<RecentMessage | null>(
    null
  );
  const observerTarget = useRef<HTMLDivElement>(null);
  const limit = 10; // Number of messages per page
  
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery<PaginatedResponse<RecentMessage[]>, Error>({
    queryKey: ["admin-recent-messages"],
    queryFn: ({ pageParam }) => apiService.getRecentMessages(pageParam as number, limit),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage.pagination;
      if (!pagination) return undefined;
      return pagination.current_page < pagination.total_pages 
        ? pagination.current_page + 1 
        : undefined;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Flatten all messages from all pages
  const messages = data?.pages.flatMap(page => page.data) || [];

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.5 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Messages</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-8 text-destructive">
            <AlertCircle className="h-4 w-4 mr-2" />
            <span>Failed to load recent messages</span>
          </div>
        ) : !messages.length ? (
          <div className="text-center text-muted-foreground py-8">
            No messages found
          </div>
        ) : (
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Content</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Metrics</TableHead>
                  <TableHead>Sent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messages.map((message) => (
                  <TableRow key={message.id}>
                    <TableCell>{message.user.name}</TableCell>
                    <TableCell>{message.account.name}</TableCell>
                    <TableCell className="max-w-[200px]">
                      <div>
                        <Badge
                          variant="outline"
                          className="capitalize flex items-center gap-1"
                        >
                          {message.content.type === "text" && (
                            <MessageSquare className="h-3 w-3" />
                          )}
                          {message.content.type === "image" && (
                            <Image className="h-3 w-3" />
                          )}
                          {message.content.type === "video" && (
                            <Video className="h-3 w-3" />
                          )}
                          {message.content.type === "audio" && (
                            <Music className="h-3 w-3" />
                          )}
                          {message.content.type}
                        </Badge>
                        <div className="truncate mt-1">
                          {message.content?.text?.body || ""}
                        </div>
                        <MediaPreview
                          message={message}
                          onClick={() => setSelectedMessage(message)}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={message.status} error_details={message.metadata?.error_details as string || ""}/>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs text-muted-foreground">
                        {message.metrics.delivery} delivered ·{" "}
                        {message.metrics.views} views
                      </div>
                    </TableCell>
                    <TableCell>
                      {message.sent_at ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                              {formatDistanceToNow(new Date(message.sent_at), {
                                addSuffix: true,
                              })}
                            </TooltipTrigger>
                            <TooltipContent>
                              {new Date(message.sent_at).toLocaleString()}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {/* Mobile View */}
        <div className="md:hidden space-y-4">
          {messages.map((message) => (
            <Card key={message.id} className="p-4">
              <div className="space-y-3">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{message.user.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {message.account.name}
                    </div>
                  </div>
                  <StatusBadge status={message.status} error_details={message.metadata?.error_details as string || ""} />
                </div>

                {/* Content */}
                <div>
                  <Badge
                    variant="outline"
                    className="capitalize flex items-center gap-1 mb-2"
                  >
                    {message.content.type === "text" && (
                      <MessageSquare className="h-3 w-3" />
                    )}
                    {message.content.type === "image" && (
                      <Image className="h-3 w-3" />
                    )}
                    {message.content.type === "video" && (
                      <Video className="h-3 w-3" />
                    )}
                    {message.content.type === "audio" && (
                      <Music className="h-3 w-3" />
                    )}
                    {message.content.type}
                  </Badge>
                  <div className="text-sm">
                    {message.content?.text?.body || ""}
                  </div>
                  <MediaPreview
                    message={message}
                    onClick={() => setSelectedMessage(message)}
                  />
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between text-sm text-muted-foreground pt-2 border-t">
                  <div>
                    {message.metrics.delivery} delivered ·{" "}
                    {message.metrics.views} views
                  </div>
                  <div>
                    {message.sent_at
                      ? formatDistanceToNow(new Date(message.sent_at), {
                          addSuffix: true,
                        })
                      : "-"}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
        {/* Loading indicator for infinite scrolling */}
        {(isFetchingNextPage || hasNextPage) && (
          <div 
            ref={observerTarget} 
            className="flex justify-center py-4 mt-4"
          >
            {isFetchingNextPage && (
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            )}
          </div>
        )}
      </CardContent>
      <MediaDialog
        message={selectedMessage}
        open={!!selectedMessage}
        onOpenChange={(open) => !open && setSelectedMessage(null)}
      />
    </Card>
  );
};
